package ui.campaign.detailv2.editor.widget

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.CampaignType
import common.frontend.api.Io
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import tools.rememberString
import ui.campaign.detailv2.editor.CampaignEditorScope
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.campaign.detailv2.editor.wrapper.ImplCampaignWrapperEVisual
import ui.widget.Dialog
import ui.widget.DialogState
import ui.widget.DialogUI
import ui.widget.Space
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds


@Suppress("UnusedReceiverParameter")
@Composable
fun CampaignEditorScope.DialogVisualDurationValidation(
    state: DialogState,
    nnvl: CampaignWrapperE,
    wrapper: ImplCampaignWrapperEVisual,
    canChangeSettings: Boolean,
    onPickDifferent: (ImplCampaignWrapperEVisual.AdapterVisual.Item) -> Unit,
    onOpenGeneralSettings: (ImplCampaignWrapperEVisual.AdapterVisual.Item, Io.Campaign.ResItem) -> Unit,
    onDurationChange: (ImplCampaignWrapperEVisual.AdapterVisual.Item, Io.Campaign.ResItem, Duration) -> Unit,
    onIncreaseDuration: (ImplCampaignWrapperEVisual.AdapterVisual.Item, Io.Campaign.ResItem, Duration) -> Unit,
    onReduceDuration: (ImplCampaignWrapperEVisual.AdapterVisual.Item, Io.Campaign.ResItem, Duration) -> Unit,
) {

    Dialog(state) {
        if (state.visible) {
            val isMobilePort = LocalDisplayProvider.current.resolutionDp.width < 200.dp
            wrapper.combinationToCheck?.also { pair ->
                DisposableEffect(Unit) {
                    onDispose {
                        wrapper.combinationToCheck = null
                    }
                }

                val newSettingImpressionValue = remember {
                    pair.second.duration.let { origin ->
                        when {
                            origin in calculateRange(nnvl.data.settings.campaignDuration, -1) -> (origin.milliseconds.inWholeSeconds - 1).seconds
                            origin in calculateRange(nnvl.data.settings.campaignDuration, 1) -> (origin.milliseconds.inWholeSeconds + 1).seconds
                            else -> (origin.milliseconds.inWholeSeconds + 1).seconds
                        }
                    }
                }

                DialogUI(
                    modifier =
                        if (isMobilePort) Modifier.fillMaxWidth()
                        else Modifier.align(Alignment.Center),
                    title = { Text(Res.string.layer_top_detail_editor__visual_validator_title.rememberString()) },
                    confirmButton = {
                        Column {
                            TextButton(
                                onClick = { onPickDifferent(pair.first) },
                                modifier = Modifier.align(Alignment.End)
                            ) {
                                Text(
                                    text = Res.string.layer_top_detail_editor__visual_validator_action_different_visual.rememberString(),
                                    textAlign = if (isMobilePort) TextAlign.Center else TextAlign.End
                                )
                            }

                            if (canChangeSettings) {
                                when (nnvl.type) {
                                    CampaignType.NONE -> Unit
                                    CampaignType.IMPRESSION -> {
                                        TextButton(
                                            onClick = {
                                                onDurationChange(pair.first, pair.second, newSettingImpressionValue)
                                            },
                                            modifier = Modifier.align(Alignment.End)
                                        ) {
                                            Text(
                                                text = Res.string.layer_top_detail_editor__visual_validator_action_change_settings_impression.rememberString(
                                                    newSettingImpressionValue
                                                ),
                                                textAlign = if (isMobilePort) TextAlign.Center else TextAlign.End
                                            )
                                        }
                                    }

                                    CampaignType.SLOT -> {
                                        TextButton(
                                            onClick = { onOpenGeneralSettings(pair.first, pair.second) },
                                            modifier = Modifier.align(Alignment.End)
                                        ) {
                                            Text(
                                                text = Res.string.layer_top_detail_editor__visual_validator_action_change_settings_slots.rememberString(),
                                                textAlign = if (isMobilePort) TextAlign.Center else TextAlign.End
                                            )
                                        }
                                    }
                                }
                            }


                            if (pair.second.duration < nnvl.data.settings.campaignDuration) {
                                TextButton(
                                    onClick = { onIncreaseDuration(pair.first, pair.second, nnvl.data.settings.campaignDuration.milliseconds) },
                                    modifier = Modifier.align(Alignment.End)
                                ) {

                                    Text(
                                        text = Res.string.layer_top_detail_editor__visual_validator_action_change_visual_increase.rememberString(
                                            nnvl.data.settings.campaignDuration.milliseconds
                                        ),
                                        textAlign = if (isMobilePort) TextAlign.Center else TextAlign.End
                                    )
                                }
                            } else {
                                TextButton(
                                    onClick = { onReduceDuration(pair.first, pair.second, nnvl.data.settings.campaignDuration.milliseconds) },
                                    modifier = Modifier.align(Alignment.End)
                                ) {

                                    Text(
                                        text = Res.string.layer_top_detail_editor__visual_validator_action_change_visual_decrease.rememberString(
                                            nnvl.data.settings.campaignDuration.milliseconds
                                        ),
                                        textAlign = if (isMobilePort) TextAlign.Center else TextAlign.End
                                    )
                                }
                            }

                        }
                    },
                    onDismissRequest = {
                        wrapper.combinationToCheck = null
                        state.hide()
                    }) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {

                        TwoFieldSwiftText(
                            label = Res.string.layer_top_detail_editor__visual_validator_label_settings_duration.rememberString(),
                            message =
                                if (nnvl.isImpressionType) nnvl.data.settings.campaignDuration.milliseconds
                                else nnvl.data.settings.let { it.campaignDuration * it.campaignDuration }.milliseconds
                        )

                        HorizontalDivider()
                        TwoFieldSwiftText(
                            label = Res.string.layer_top_detail_editor__visual_validator_label_current_duration.rememberString(),
                            message = pair.second.duration.milliseconds
                        )
                        HorizontalDivider()
                        TwoFieldSwiftText(
                            label = Res.string.layer_top_detail_editor__visual_validator_label_range_duration.rememberString(),
                            message = "${pair.first.pickerArgs.restrictionDurationMin.milliseconds} - ${pair.first.pickerArgs.restrictionDurationMax.milliseconds}"
                        )

                        Space(8.dp)
                        Text(
                            text = Res.string.layer_top_detail_editor__visual_validator_label_demand.rememberString(),
                            style = MaterialTheme.typography.titleLarge
                        )
                    }
                }
            }
        }
    }
}

private fun calculateRange(originMs: Long, extraSec: Int): LongRange {
    return (originMs.milliseconds.inWholeSeconds + extraSec).seconds.inWholeMilliseconds.let {
        (it.toDouble() * 0.9).toLong()..(it.toDouble() * 1.1).toLong()
    }
}